import { BaseDataListResponse } from 'Base'
import axiosInstant from 'api/baseRequest'
import clsx from 'clsx'
import { Button } from 'primereact/button'
import { MultiSelect } from 'primereact/multiselect'
import { Tag } from 'primereact/tag'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { LIMIT_NOT_LAZY_SELECT } from 'utils/constants/commons-constant'
import { REACT_APP_SERVER_API } from 'utils/constants/environment'

import LoadingDot from 'components/LoadingDot'

interface ScopeSelectboxProps {
  params?: any
  url?: string
  baseURL?: string
  method?: string
  tranformData?: (data: any) => any[]
  onChange: (selected: string[]) => void
  value: string[]
  placeholder?: string
  className?: string
  optionLabel?: string
  optionValue?: string
  // 'default' = tag-colored chip (warning). 'neutral' = gray chip, for
  // options that aren't Tag entities (e.g. Branch, Event) so they read as
  // visually distinct from a real Tag.
  chipVariant?: 'default' | 'neutral'
  [key: string]: any
}

const ScopeSelectbox = forwardRef((props: ScopeSelectboxProps, ref: any) => {
  const {
    url = '/',
    baseURL = REACT_APP_SERVER_API,
    params,
    method = 'get',
    tranformData,
    value,
    onChange,
    optionValue = '_id',
    optionLabel = 'name',
    chipVariant = 'default',
    ...restProps
  } = props

  const [data, setData] = useState([])
  const [selected, setSelected] = useState([])
  const [loading, setLoading] = useState(false)

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axiosInstant.request<BaseDataListResponse<any>>({
        url,
        method,
        baseURL,
        params: params ?? { perPage: LIMIT_NOT_LAZY_SELECT },
      })
      setData(data.data || [])
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [baseURL, method, params, url])

  useEffect(() => {
    getData()
  }, [getData])

  const { t } = useTranslation()

  const realOptions = tranformData ? tranformData(data) : data

  // The form value may hold either raw ids or populated objects, depending on
  // whether the form was hydrated from an existing newsletter.
  const valSelectedIds = (value || []).map((item: any) =>
    typeof item === 'object' && item !== null
      ? item[optionValue] || item._id
      : item
  )

  const valueKey = valSelectedIds.join(',')

  useEffect(() => {
    setSelected(valSelectedIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey])

  const panelFooterTemplate = () => {
    return (
      <div className="py-3 px-3 flex justify-content-end gap-2 p-column-filter-buttonbar">
        <Button outlined onClick={() => setSelected([])}>
          {t('Clear')}
        </Button>
        <Button
          onClick={async () => {
            onChange(selected)
            await Promise.resolve()
            innerRef.current?.hide()
          }}
        >
          {t('Apply')}
        </Button>
      </div>
    )
  }

  const innerRef = useRef<any>(null)
  useImperativeHandle(ref, () => innerRef.current, [])

  const renderSelectedItem = (option: any, optionsProps?: any) => {
    if (!option) return null

    const optionId =
      typeof option === 'object' && option !== null
        ? option[optionValue] || option._id
        : option

    if (loading) {
      // Options aren't resolvable yet, so show a single placeholder for the
      // whole selection rather than one per item.
      const currentSelectedValues = optionsProps?.value || selected || []
      if (currentSelectedValues.indexOf(optionId) > 0) return null

      return <LoadingDot className="inline-flex align-items-center py-1 px-1" />
    }

    // `optionValue` means the model holds bare ids — resolve back to the option
    // object to get a label. `value` is searched too so an item that dropped out
    // of the fetched list still renders with the name it was saved under.
    let itemObj = option
    if (typeof option !== 'object' || option === null || !option[optionLabel]) {
      const optionsToSearch = [
        ...(optionsProps?.options || realOptions || []),
        ...(Array.isArray(value) ? value : []),
      ]
      const found = optionsToSearch.find(
        (item: any) =>
          item && (item[optionValue] === optionId || item._id === optionId)
      )
      if (found) itemObj = found
    }

    const displayName =
      typeof itemObj === 'object' && itemObj !== null
        ? itemObj[optionLabel] || itemObj.name || itemObj.title || optionId
        : String(itemObj)

    return (
      <Tag
        severity="warning"
        className={clsx(
          'font-medium mr-1 my-0 inline-flex align-items-center',
          chipVariant === 'neutral' && 'p-tag-inactive'
        )}
        style={{ padding: '2px 6px', fontSize: '12px', height: '22px' }}
      >
        {displayName}
      </Tag>
    )
  }

  return (
    <MultiSelect
      ref={innerRef}
      options={realOptions}
      optionLabel={optionLabel}
      display="comma"
      loading={loading}
      placeholder="Select an option"
      filter
      // `selectAll`/`onSelectAll` are deliberately left to PrimeReact: passing
      // either one opts out of its native handling, which scopes "select all"
      // to the currently filtered options and derives the header checkbox itself.
      showSelectAll
      // Closing the panel without pressing Apply discards the pending edits.
      onHide={() => setSelected(valSelectedIds)}
      emptyFilterMessage={t('No results founds')}
      emptyMessage={t('No available options')}
      optionValue={optionValue}
      panelClassName="w-25rem"
      className="w-full max-w-20rem"
      panelFooterTemplate={panelFooterTemplate}
      value={selected}
      onChange={(e) => setSelected(e.target.value || [])}
      tooltip={undefined}
      tooltipOptions={{ disabled: true }}
      {...restProps}
      selectedItemTemplate={renderSelectedItem}
    />
  )
})
export default ScopeSelectbox
